import { ThemeEvents } from '@theme/events';

/**
 * Keeps <wishlist-button-pdp>'s data-variant-id / data-preselected-id in
 * sync with the currently selected variant, mirroring the same pattern
 * product-card.js already uses for PLP card wishlist buttons on swatch
 * selection. Fish Wishlist's own app script owns the button's behavior -
 * this only keeps its variant reference current.
 */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const newVariantId = event.detail?.resource?.id;
  if (!newVariantId) return;

  const section = event.target instanceof Element ? event.target.closest('.shopify-section, dialog') : null;
  const wishlistButtons = section
    ? section.querySelectorAll('wishlist-button-pdp')
    : document.querySelectorAll('wishlist-button-pdp');

  wishlistButtons.forEach((button) => {
    button.setAttribute('data-variant-id', String(newVariantId));
    button.setAttribute('data-preselected-id', String(newVariantId));
  });
});

/**
 * The header's <open-wishlists-button data-primary> renders its own
 * .wishlist-count-bubble once, on page load. Fish Wishlist's own script
 * (assets/fish-wishlist.js, loaded via their app embed) exposes the real
 * events it dispatches on `document` as `window.FishWishlist.events`
 * (confirmed live: ITEM_ADDED: "fishwishlist:item-added-to-wishlist",
 * ITEM_REMOVED: "fishwishlist:item-removed-from-wishlist" — same strings
 * their own minified source dispatches via `document.dispatchEvent(new
 * CustomEvent(...))`), and their button custom element exposes a real
 * `.setCount()` instance method (confirmed live) that re-reads the
 * current wishlist and updates its own bubble — the exact same function
 * their own `document.querySelectorAll("open-wishlists-button[data-primary]").forEach(t => t.setCount())`
 * helper calls internally. Without this listener, adding/removing an
 * item elsewhere (a PDP/PLP wishlist-button-pdp click) never told the
 * HEADER's own separate button instance to re-run that method, so its
 * bubble only ever caught up on the next full page load. Hardcoded
 * string fallbacks (matching the confirmed values above) are used
 * instead of reading `window.FishWishlist.events` directly so this still
 * works if this script happens to run before their app-embed script has
 * finished initializing that global.
 */
const FISHWISHLIST_ITEM_ADDED = 'fishwishlist:item-added-to-wishlist';
const FISHWISHLIST_ITEM_REMOVED = 'fishwishlist:item-removed-from-wishlist';
const FISHWISHLIST_LOADED = 'fishwishlist:loaded';

function refreshHeaderWishlistCount() {
  document.querySelectorAll('open-wishlists-button[data-primary]').forEach((button) => {
    button.setCount?.();
  });
}

/**
 * Fish Wishlist's own wishlist-button / wishlist-button-pdp elements
 * already compute their `data-is-added` attribute from their own real
 * `window.FishWishlist.isInAnyWishlist(variantId)` method (confirmed live
 * and via source inspection) - but only once, inside their own
 * connectedCallback, at the moment each button first connects. Nothing
 * re-runs that check when the wishlist changes from a DIFFERENT button or
 * instance (removing from the wishlist drawer while a PDP/card button for
 * the same product is still on screen, another card for the same product
 * elsewhere on the page, etc.), so a button's icon only ever reflects the
 * state it saw when it connected. The actual empty/added icon swap is
 * driven purely by their own stylesheet's `[data-is-added]` attribute
 * selector (confirmed live: toggling this attribute alone flips the
 * visible icon, no re-render needed) - so re-deriving this attribute with
 * their own real isInAnyWishlist() method is enough to resync it.
 */
function syncWishlistIconState(el) {
  const isInAnyWishlist = window.FishWishlist?.isInAnyWishlist;
  const variantId = el.dataset.variantId;
  if (typeof isInAnyWishlist !== 'function' || !variantId) return;

  try {
    // `window.FishWishlist` (and its isInAnyWishlist method) can exist
    // before their app has finished loading the customer's wishlist data
    // (confirmed live: isInAnyWishlist itself threw "Cannot read
    // properties of undefined (reading 'allWishlistItems')" on a fresh
    // page load) - since this can run outside an event handler (see the
    // startup recovery call below), an uncaught throw here would silently
    // abort the rest of this module, breaking every listener defined
    // after it. Not a Fish Wishlist bug we can fix - just guarding
    // against their own documented-as-ready-but-not-yet-loaded state.
    const isAdded = String(isInAnyWishlist(variantId));
    if (el.dataset.isAdded !== isAdded) {
      el.dataset.isAdded = isAdded;
    }
  } catch (_) {
    // Not ready yet - the fishwishlist:loaded listener below will retry.
  }
}

function syncAllWishlistIcons() {
  document.querySelectorAll('wishlist-button, wishlist-button-pdp').forEach(syncWishlistIconState);
}

/**
 * Fish Wishlist's own connectedCallback renders each button's empty/added
 * SVG markup from `this.app.settings.icon.*` exactly once, synchronously,
 * at connect time (confirmed via source inspection of their button
 * class's render()/connectedCallback). A button whose Liquid markup is
 * already in the initial HTML can connect (and call render()) before
 * their app-embed script has finished loading its config/settings -
 * nothing ever re-renders it afterwards, leaving the icon permanently
 * empty. Their own real `fishwishlist:loaded` event (confirmed in
 * window.FishWishlist.events) fires once their app has fully
 * initialized, so re-running their own real `.render()` method then
 * recovers any icon that connected too early - using their own method,
 * not reimplementing their markup.
 */
function reRenderWishlistIcons() {
  document.querySelectorAll('wishlist-button, wishlist-button-pdp').forEach((el) => {
    el.render?.();
  });
  syncAllWishlistIcons();
}

document.addEventListener(FISHWISHLIST_ITEM_ADDED, () => {
  refreshHeaderWishlistCount();
  syncAllWishlistIcons();
});
document.addEventListener(FISHWISHLIST_ITEM_REMOVED, () => {
  refreshHeaderWishlistCount();
  syncAllWishlistIcons();
});
document.addEventListener(FISHWISHLIST_LOADED, reRenderWishlistIcons);

if (Array.isArray(window.FishWishlist?.allWishlistItems)) {
  // This module can run after Fish Wishlist's own app-embed script has
  // already finished initializing - in that case their `loaded` event
  // fired before this listener was attached, so run the same recovery
  // once now. `window.FishWishlist` existing is NOT proof of this by
  // itself (confirmed live: it can exist as an object before their
  // wishlist data has loaded into it) - `allWishlistItems` actually being
  // populated is the real signal.
  reRenderWishlistIcons();
}

/**
 * Deleting an entire wishlist (as opposed to removing a single item from
 * one) goes through a different Fish Wishlist code path that dispatches no
 * `document` event and never calls `.setCount()` itself (confirmed live:
 * deleting a whole wishlist via its own trash icon produced zero fired
 * events and zero `.setCount()` calls, leaving the header bubble stale).
 * There's no native event to listen for here, so this delegates a click
 * listener to their own real `.wishlist-drawer__delete-button` element
 * (confirmed present in the live DOM) and still finishes the update by
 * calling their own real `.setCount()` via the same helper above - just
 * triggered differently. The deletion itself is async, so the refresh is
 * deferred slightly to let their own app finish updating its storage
 * before `.setCount()` / `isInAnyWishlist()` re-read it. Also resyncs
 * every visible wishlist icon for the same reason as above - a deleted
 * wishlist can contain products whose PDP/card icon is currently showing
 * "added".
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('.wishlist-drawer__delete-button')) return;

  setTimeout(() => {
    refreshHeaderWishlistCount();
    syncAllWishlistIcons();
  }, 300);
});
